import {
  BOARD_THEMES,
  PIECE_MATERIALS,
  PIECE_SIZES,
  PIECE_THEMES,
  WHITE_PIECE_COLORS,
  BLACK_PIECE_COLORS,
} from '@chessmate/board-3d';
import { Button, Segmented, Swatch, ThemeTile } from '@chessmate/ui';
import {
  matchAssistancePreset,
  type AssistancePresetId,
} from '../state/assistance-presets.js';
import { TABLE_THEMES, useTableThemeStore } from '../state/table-theme-store.js';
import {
  CARD_DESIGNS,
  CARD_DESIGN_NAMES,
  useCardDesignStore,
  type CardDesign,
} from '../state/card-design-store.js';
import { CARD_FACE_NAMES, CARD_FACE_STYLES, useCardFaceStore } from '../state/card-face-store.js';
import { CardFace, type CardFaceStyle } from './CardFace.js';
import { edition } from '../edition/index.js';
import { CARD_PACKS, CLASSIC_PACK, packsForEdition } from '../lib/card-packs.js';
import { MARKETPLACE_URL } from '../services/api.js';
import { useActivePack, useCardPackStore } from '../state/card-pack-store.js';
import { useCommunityPackStore } from '../state/community-pack-store.js';
import { CommunityPackAdder } from './CommunityPackAdder.js';
import {
  useThemeStore,
  type LookaheadDepth,
  type TableMaterialId,
  type TableToneId,
} from '../state/theme-store.js';

const MATERIAL_OPTIONS = PIECE_MATERIALS.map((m) => ({ value: m.id, label: m.name }));
const SIZE_OPTIONS = PIECE_SIZES.map((s) => ({ value: s.id, label: s.name }));
const TABLE_MATERIAL_OPTIONS = [
  { value: 'wood' as const, label: 'Wood' },
  { value: 'stone' as const, label: 'Stone' },
];
const TABLE_TONE_OPTIONS = [
  { value: 'light' as const, label: 'Light' },
  { value: 'dark' as const, label: 'Dark' },
];
// Segmented is generic over string, so we render depth as '1' / '2' / '3'
// and translate to/from the number type at the store boundary.
const LOOKAHEAD_DEPTH_OPTIONS = [
  { value: '1' as const, label: '1 ply' },
  { value: '2' as const, label: '2 ply' },
  { value: '3' as const, label: '3 ply' },
];

const PRESET_OPTIONS = [
  { value: 'beginner' as const, label: 'Beginner' },
  { value: 'medium' as const, label: 'Medium' },
  { value: 'advanced' as const, label: 'Advanced' },
];

function depthFromString(v: string): LookaheadDepth {
  const n = Number(v);
  return n === 2 || n === 3 ? n : 1;
}

export function ThemePicker() {
  const cardDesign = useCardDesignStore((s) => s.design);
  const setCardDesign = useCardDesignStore((s) => s.setDesign);
  const cardFace = useCardFaceStore((s) => s.style);
  const setCardFace = useCardFaceStore((s) => s.setStyle);
  const tableTheme = useTableThemeStore((s) => s.theme);
  const setTableTheme = useTableThemeStore((s) => s.setTheme);
  const pieceThemeId = useThemeStore((s) => s.pieceThemeId);
  const boardThemeId = useThemeStore((s) => s.boardThemeId);
  const setPieceTheme = useThemeStore((s) => s.setPieceTheme);
  const setBoardTheme = useThemeStore((s) => s.setBoardTheme);
  const tableMaterialId = useThemeStore((s) => s.tableMaterialId);
  const tableToneId = useThemeStore((s) => s.tableToneId);
  const setTableMaterial = useThemeStore((s) => s.setTableMaterial);
  const setTableTone = useThemeStore((s) => s.setTableTone);
  const pieceMaterialId = useThemeStore((s) => s.pieceMaterialId);
  const pieceSizeId = useThemeStore((s) => s.pieceSizeId);
  const pieceWhiteColor = useThemeStore((s) => s.pieceWhiteColor);
  const pieceBlackColor = useThemeStore((s) => s.pieceBlackColor);
  const setPieceMaterial = useThemeStore((s) => s.setPieceMaterial);
  const setPieceSize = useThemeStore((s) => s.setPieceSize);
  const setPieceWhiteColor = useThemeStore((s) => s.setPieceWhiteColor);
  const setPieceBlackColor = useThemeStore((s) => s.setPieceBlackColor);
  const showCoords = useThemeStore((s) => s.showCoords);
  const showMinimap = useThemeStore((s) => s.showMinimap);
  const lookaheadEnabled = useThemeStore((s) => s.lookaheadEnabled);
  const lookaheadDepth = useThemeStore((s) => s.lookaheadDepth);
  const setShowCoords = useThemeStore((s) => s.setShowCoords);
  const setShowMinimap = useThemeStore((s) => s.setShowMinimap);
  const setLookaheadEnabled = useThemeStore((s) => s.setLookaheadEnabled);
  const setLookaheadDepth = useThemeStore((s) => s.setLookaheadDepth);
  const highlightMovable = useThemeStore((s) => s.highlightMovable);
  const setHighlightMovable = useThemeStore((s) => s.setHighlightMovable);
  const showMovePaths = useThemeStore((s) => s.showMovePaths);
  const setShowMovePaths = useThemeStore((s) => s.setShowMovePaths);
  const warnUnsafeMoves = useThemeStore((s) => s.warnUnsafeMoves);
  const setWarnUnsafeMoves = useThemeStore((s) => s.setWarnUnsafeMoves);
  const applyAssistancePreset = useThemeStore((s) => s.applyAssistancePreset);
  const activePreset = matchAssistancePreset({
    highlightMovable,
    showMovePaths,
    warnUnsafeMoves,
    showCoords,
    showMinimap,
    lookaheadEnabled,
    lookaheadDepth,
  });

  return (
    <div className="w-full rounded-3xl border-2 border-sun/30 bg-cream p-4 shadow-lg" data-testid="theme-picker">
      <h2 className="mb-3 text-xl font-extrabold text-ink">Board look</h2>

      <div className="mb-3 flex flex-col gap-1">
        <p className="text-sm font-bold text-ink">Pieces</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Piece theme">
          {PIECE_THEMES.map((t) => (
            <ThemeTile
              key={t.id}
              id={`piece-${t.id}`}
              label={t.name}
              previewSrc={t.preview}
              selected={pieceThemeId === t.id}
              onClick={() => setPieceTheme(t.id)}
            />
          ))}
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-1">
        <p className="text-sm font-bold text-ink">Board</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Board theme">
          {BOARD_THEMES.map((t) => (
            <ThemeTile
              key={t.id}
              id={`board-${t.id}`}
              label={t.name}
              previewSrc={t.preview}
              selected={boardThemeId === t.id}
              onClick={() => setBoardTheme(t.id)}
            />
          ))}
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-2">
        <p className="text-sm font-bold text-ink">Piece material</p>
        <Segmented
          options={MATERIAL_OPTIONS}
          value={pieceMaterialId}
          onChange={setPieceMaterial}
          testid="piece-material"
          ariaLabel="Piece material"
        />
      </div>

      <div className="mb-3 flex flex-col gap-2">
        <p className="text-sm font-bold text-ink">Piece size</p>
        <Segmented
          options={SIZE_OPTIONS}
          value={pieceSizeId}
          onChange={setPieceSize}
          testid="piece-size"
          ariaLabel="Piece size"
        />
      </div>

      <div className="mb-3 flex flex-col gap-2">
        <p className="text-sm font-bold text-ink">White side color</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="White side color">
          {WHITE_PIECE_COLORS.map((c) => (
            <Swatch
              key={c.id}
              id={`piece-white-${c.id}`}
              label={c.label}
              color={c.hex}
              selected={pieceWhiteColor === c.hex}
              onClick={() => setPieceWhiteColor(c.hex)}
            />
          ))}
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-2">
        <p className="text-sm font-bold text-ink">Black side color</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Black side color">
          {BLACK_PIECE_COLORS.map((c) => (
            <Swatch
              key={c.id}
              id={`piece-black-${c.id}`}
              label={c.label}
              color={c.hex}
              selected={pieceBlackColor === c.hex}
              onClick={() => setPieceBlackColor(c.hex)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-bold text-ink">Table</p>
        <Segmented<TableMaterialId>
          options={TABLE_MATERIAL_OPTIONS}
          value={tableMaterialId}
          onChange={setTableMaterial}
          testid="table-material"
          ariaLabel="Table material"
        />
        <Segmented<TableToneId>
          options={TABLE_TONE_OPTIONS}
          value={tableToneId}
          onChange={setTableTone}
          testid="table-tone"
          ariaLabel="Table tone"
        />
      </div>

      <div className="mt-3 flex flex-col gap-2 border-t border-sun/20 pt-3">
        <p className="text-sm font-bold text-ink">Teaching aids</p>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold text-ink/70">
            Level{activePreset === null ? ' (custom)' : ''}
          </p>
          <Segmented
            options={PRESET_OPTIONS}
            value={activePreset ?? ''}
            onChange={(v) => applyAssistancePreset(v as AssistancePresetId)}
            testid="assistance-preset"
            ariaLabel="Assistance level"
          />
        </div>
        <ToggleRow
          testid="highlight-movable"
          label="Highlight movable pieces"
          description="Ring the pieces you can move this turn"
          on={highlightMovable}
          onChange={setHighlightMovable}
        />
        <ToggleRow
          testid="show-move-paths"
          label="Show move paths"
          description="Trace where a selected piece travels; stronger end squares"
          on={showMovePaths}
          onChange={setShowMovePaths}
        />
        <ToggleRow
          testid="warn-unsafe-moves"
          label="Warn about unsafe moves"
          description="! = piece lost (or backgammon checker open to a single-die hit), ? = losing trade (or a combined-roll shot)"
          on={warnUnsafeMoves}
          onChange={setWarnUnsafeMoves}
        />
        <ToggleRow
          testid="show-coords"
          label="Show coordinates"
          description="a-h / 1-8 labels at the board edge"
          on={showCoords}
          onChange={setShowCoords}
        />
        <ToggleRow
          testid="show-minimap"
          label="Show minimap"
          description="2D position summary in the corner"
          on={showMinimap}
          onChange={setShowMinimap}
        />
        <ToggleRow
          testid="lookahead"
          label="Look-ahead"
          description="Hint at the opponent's likely reply"
          on={lookaheadEnabled}
          onChange={setLookaheadEnabled}
        />
        {lookaheadEnabled && (
          <div className="flex flex-col gap-1 pl-1">
            <p className="text-xs font-bold text-ink/70">Hint depth</p>
            <Segmented
              options={LOOKAHEAD_DEPTH_OPTIONS}
              value={String(lookaheadDepth)}
              onChange={(v) => setLookaheadDepth(depthFromString(v))}
              testid="lookahead-depth"
              ariaLabel="Look-ahead depth"
            />
          </div>
        )}
      </div>
    <div className="mt-4">
      <h2 className="mb-3 text-xl font-extrabold text-ink">Table theme</h2>
      <Segmented
        options={TABLE_THEMES.map((th) => ({ value: th, label: th === 'default' ? 'Default' : (th[0] ?? '').toUpperCase() + th.slice(1) }))}
        value={tableTheme}
        onChange={setTableTheme}
        testid="table-theme"
        ariaLabel="Table theme"
      />
    </div>
    <div className="mt-4">
      <h2 className="mb-3 text-xl font-extrabold text-ink">Card back</h2>
      <Segmented<CardDesign>
        options={CARD_DESIGNS.map((d) => ({ value: d, label: CARD_DESIGN_NAMES[d] }))}
        value={cardDesign}
        onChange={setCardDesign}
        ariaLabel="Card back"
        testid="card-back-design"
      />
    </div>
    <div className="mt-4">
      <h2 className="mb-3 text-xl font-extrabold text-ink">Card face</h2>
      <Segmented<CardFaceStyle>
        options={CARD_FACE_STYLES.map((d) => ({ value: d, label: CARD_FACE_NAMES[d] }))}
        value={cardFace}
        onChange={setCardFace}
        ariaLabel="Card face"
        testid="card-face-style"
      />
    </div>
    <CardPackPicker />
    </div>
  );
}

function ToggleRow({
  testid,
  label,
  description,
  on,
  onChange,
}: {
  readonly testid: string;
  readonly label: string;
  readonly description: string;
  readonly on: boolean;
  readonly onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col">
        <span className="text-sm font-bold text-ink">{label}</span>
        <span className="text-xs text-ink/50">{description}</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        data-testid={testid}
        data-on={on ? 'true' : 'false'}
        onClick={() => onChange(!on)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
          on ? 'bg-berry' : 'bg-ink/20'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-cream shadow transition-transform ${
            on ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

const PACK_PREVIEW = [
  { rank: 11, suit: 'H' },
  { rank: 12, suit: 'S' },
  { rank: 13, suit: 'H' },
  { rank: 14, suit: 'S' },
] as const;

/** Card pack: alternative art for Jack, Queen, King and Ace, with a four-card preview of the pick. */
function CardPackPicker() {
  const packId = useCardPackStore((s) => s.packId);
  const setPack = useCardPackStore((s) => s.setPack);
  const active = useActivePack();
  const community = useCommunityPackStore((s) => s.packs);
  const removeCommunity = useCommunityPackStore((s) => s.remove);
  const offered = [...packsForEdition(edition.id), ...packsForEdition(edition.id, community)];
  // Adding from a link is adult-edition only for now: the child edition gets it behind the parent gate (marketplace README, next slices).
  const canAdd = MARKETPLACE_URL !== '' && edition.id === 'adult';
  if (CARD_PACKS.length === 0 || offered.length === 0) return null;
  return (
    <div className="mt-4" data-testid="card-pack-picker">
      <h2 className="mb-3 text-xl font-extrabold text-ink">Card pack</h2>
      <Segmented<string>
        options={[{ value: CLASSIC_PACK, label: 'Classic' }, ...offered.map((p) => ({ value: p.id, label: p.name }))]}
        value={active ? packId : CLASSIC_PACK}
        onChange={setPack}
        ariaLabel="Card pack"
        testid="card-pack"
      />
      <p className="mt-2 text-base font-semibold text-fg-muted" data-testid="card-pack-tagline">
        {active ? active.tagline || (active.author ? `By ${active.author}.` : '') : 'The built-in deck.'}
      </p>
      {active?.family === 'community' ? (
        <Button variant="secondary" className="mt-2" data-testid="community-pack-remove" onClick={() => { setPack(CLASSIC_PACK); removeCommunity(active.id); }}>
          Remove this pack
        </Button>
      ) : null}
      {canAdd ? <CommunityPackAdder edition={edition.id} marketplaceUrl={MARKETPLACE_URL} /> : null}
      <div className="mt-3 flex gap-2" data-testid="card-pack-preview" aria-hidden="true">
        {PACK_PREVIEW.map((c) => (
          <div key={`${c.rank}${c.suit}`} className={`w-20 rounded-lg bg-white p-0.5 shadow ${c.suit === 'H' ? 'text-rose-600' : 'text-slate-900'}`}>
            <CardFace rank={c.rank} suit={c.suit} />
          </div>
        ))}
      </div>
    </div>
  );
}
